/**
 * Minimal fetch wrapper. Responsibilities:
 *   - Inject `Authorization: Bearer <token>` from a TokenProvider.
 *   - Inject a correlation id (`x-correlation-id`) on every request.
 *   - On 401, attempt a single refresh, then retry the original request
 *     once. Concurrent 401s share a single refresh promise (single-flight)
 *     so we never thrash the refresh endpoint.
 *   - Normalise non-2xx into `ApiError` carrying RFC 7807 problem details.
 *
 * The client is intentionally framework-free — it works in admin-web, in
 * Node tests, and (with a polyfilled fetch) in React Native.
 */
import { ApiError, NetworkError } from "./errors.js";
export class ApiClient {
    baseUrl;
    tokens;
    onUnauth;
    correlationId;
    fetchImpl;
    /** Single-flight refresh promise so parallel 401s share work. */
    refreshing = null;
    constructor(opts) {
        this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
        this.tokens = opts.tokens;
        if (opts.onUnauthenticated)
            this.onUnauth = opts.onUnauthenticated;
        this.correlationId = opts.correlationId ?? (() => crypto.randomUUID());
        this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    }
    get(path, opts) {
        return this.request("GET", path, undefined, opts);
    }
    post(path, body, opts) {
        return this.request("POST", path, body, opts);
    }
    put(path, body, opts) {
        return this.request("PUT", path, body, opts);
    }
    patch(path, body, opts) {
        return this.request("PATCH", path, body, opts);
    }
    delete(path, opts) {
        return this.request("DELETE", path, undefined, opts);
    }
    async request(method, path, body, opts) {
        const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
        const headers = {
            "x-correlation-id": this.correlationId(),
            accept: "application/json",
            ...(body !== undefined ? { "content-type": "application/json" } : {}),
            ...(opts?.headers ?? {}),
        };
        if (!opts?.public) {
            const access = this.tokens.getAccessToken();
            if (access)
                headers["authorization"] = `Bearer ${access}`;
        }
        let response;
        try {
            response = await this.fetchImpl(url, {
                method,
                headers,
                ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
            });
        }
        catch (err) {
            throw new NetworkError(`Network request to ${method} ${path} failed`, err);
        }
        // 401: attempt single-flight refresh, then retry once.
        if (response.status === 401 && !opts?.public) {
            const newAccess = await this.runRefresh();
            if (newAccess) {
                headers["authorization"] = `Bearer ${newAccess}`;
                try {
                    response = await this.fetchImpl(url, {
                        method,
                        headers,
                        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
                    });
                }
                catch (err) {
                    throw new NetworkError(`Retry of ${method} ${path} failed`, err);
                }
            }
        }
        if (response.status === 204 || response.status === 205) {
            return undefined;
        }
        const text = await response.text();
        const parsed = text ? safeParseJson(text) : undefined;
        if (!response.ok) {
            const problem = parsed && typeof parsed === "object"
                ? parsed
                : { detail: text || response.statusText };
            throw new ApiError(response.status, problem);
        }
        return (parsed ?? undefined);
    }
    runRefresh() {
        if (this.refreshing)
            return this.refreshing;
        const refresh = this.tokens.getRefreshToken();
        if (!refresh) {
            this.tokens.clearTokens();
            this.onUnauth?.();
            return Promise.resolve(null);
        }
        this.refreshing = (async () => {
            try {
                const res = await this.fetchImpl(`${this.baseUrl}/api/auth/refresh`, {
                    method: "POST",
                    headers: {
                        accept: "application/json",
                        "content-type": "application/json",
                        "x-correlation-id": this.correlationId(),
                    },
                    body: JSON.stringify({
                        refreshToken: refresh,
                        deviceFingerprint: this.tokens.getDeviceFingerprint(),
                    }),
                });
                if (!res.ok) {
                    this.tokens.clearTokens();
                    this.onUnauth?.();
                    return null;
                }
                const next = (await res.json());
                this.tokens.saveTokens(next);
                return next.accessToken;
            }
            catch {
                this.tokens.clearTokens();
                this.onUnauth?.();
                return null;
            }
            finally {
                this.refreshing = null;
            }
        })();
        return this.refreshing;
    }
}
function safeParseJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=client.js.map